import React from 'react';
import dayjs from 'dayjs';

// material-ui
import { Autocomplete, Box, Checkbox, FormControlLabel, FormHelperText, InputAdornment, MenuItem, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { LetterField } from '../dataObjects/LetterType';

interface LetterFieldInputProps {
    field: LetterField;
    value: string;
    showError: boolean;
    disabled?: boolean;         // waived by a checked box: greyed out, not required
    onChange: (key: string, value: string) => void;
}

/** One form control for a letter field; the control comes from the field's type. Dates are held as YYYY-MM-DD. */
const LetterFieldInput = (props: LetterFieldInputProps) => {
    const { field, value, showError } = props;
    const disabled = props.disabled ?? false;
    const label = field.label + (field.isRequired && !disabled ? ' *' : '');
    const helperText = showError ? `${field.label} is required` : field.helpText ?? undefined;

    const change = (newValue: string) => props.onChange(field.key, newValue);

    switch (field.type) {
        case 'Checkbox':
            // held as "true" or "", like any other value
            return (
                <Box>
                    <FormControlLabel
                        label={field.label}
                        control={<Checkbox size="small" disabled={disabled} checked={value === 'true'} onChange={(e) => change(e.target.checked ? 'true' : '')} />}
                    />
                    {helperText && <FormHelperText sx={{ marginTop: -0.5, marginLeft: 4 }}>{helperText}</FormHelperText>}
                </Box>
            );

        case 'Date':
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {/* clicking the calendar's month heading opens a year list, then a month grid, to jump without paging month by month */}
                    <DatePicker
                        views={['year', 'month', 'day']}
                        label={label}
                        disabled={disabled}
                        value={value ? dayjs(value) : null}
                        onChange={(date) => change(date && date.isValid() ? date.format('YYYY-MM-DD') : '')}
                        slotProps={{ textField: { size: 'small', fullWidth: true, error: showError, helperText: helperText } }}
                    />
                </LocalizationProvider>
            );

        case 'Money':
            return (
                <TextField
                    label={label}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    value={value}
                    error={showError}
                    helperText={helperText}
                    onChange={(e) => change(e.target.value.replace(/[^0-9.,]/g, ''))}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    inputProps={{ inputMode: 'decimal' }}
                />
            );

        case 'Select':
            if (field.allowCustom) {
                // pick a choice or type something else; every keystroke counts, no Enter needed
                return (
                    <Autocomplete
                        freeSolo
                        disabled={disabled}
                        options={field.options ?? []}
                        inputValue={value}
                        onInputChange={(e, newValue) => change(newValue)}
                        renderInput={(params) =>
                            <TextField {...params} label={label} size="small" fullWidth error={showError} helperText={helperText} />
                        }
                    />
                );
            }
            return (
                <TextField
                    select
                    label={label}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    value={value}
                    error={showError}
                    helperText={helperText}
                    onChange={(e) => change(e.target.value)}
                >
                    {/* an optional choice can be cleared */}
                    {!field.isRequired && <MenuItem value=""><em>None</em></MenuItem>}
                    {(field.options ?? []).map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
            );

        default:
            return (
                <TextField
                    label={label}
                    disabled={disabled}
                    size="small"
                    fullWidth
                    multiline={field.type === 'MultilineText'}
                    minRows={field.type === 'MultilineText' ? 3 : undefined}
                    value={value}
                    error={showError}
                    helperText={helperText}
                    onChange={(e) => change(e.target.value)}
                />
            );
    }
};

export default LetterFieldInput;
